<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { History } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { Skeleton } from "@/components/ui/skeleton";
  import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
  import HistorySession from "@/components/Survey/History/HistorySession.vue";
  import { dayLabelFrom, shortDateTimeFrom } from "@utils/time";
  import { useSnapshotsStore } from "@stores/survey/snapshots";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useCardsStore } from "@stores/survey/cards";
  import { useSectionsStore } from "@stores/survey/sections";
  import type { Snapshot } from "@/types";

  const snapshotsStore = useSnapshotsStore();
  const { snapshots, loading, restoringId } = storeToRefs(snapshotsStore);
  const { survey, canWriteSurvey, hasSurveyChanged } = storeToRefs(useSurveyStore());
  const { hasUnsavedChanges: hasCardsChanged } = storeToRefs(useCardsStore());
  const { hasUnsavedChanges: hasSectionsChanged } = storeToRefs(useSectionsStore());

  const open = ref(false);
  const pending = ref<Snapshot | null>(null);

  const isDraft = computed(() => ["draft", "review"].includes(survey.value?.status));
  const hasUnsavedChanges = computed(() => hasSurveyChanged.value || hasCardsChanged.value || hasSectionsChanged.value);
  const restoreDisabledReason = computed(() => {
    if (!isDraft.value) return "Only a draft questionnaire can be restored";
    if (hasUnsavedChanges.value) return "Save or discard your changes first";
    return null;
  });

  const sessions = computed(() => {
    const groups: { key: string; day: string; versions: Snapshot[] }[] = [];
    snapshots.value.forEach((snapshot) => {
      const last = groups.at(-1);
      if (last?.key === snapshot.session_key) last.versions.push(snapshot);
      else groups.push({ key: snapshot.session_key, day: dayLabelFrom(snapshot.updated_at), versions: [snapshot] });
    });
    return groups;
  });

  const pendingLabel = computed(() =>
    pending.value
      ? `${shortDateTimeFrom(pending.value.updated_at)}${pending.value.user ? ` by ${pending.value.user.full_name}` : ""}`
      : "",
  );

  const confirmRestore = async () => {
    if (!pending.value) return;
    await snapshotsStore.restore(pending.value);
    pending.value = null;
    open.value = false;
  };

  watch(open, (isOpen) => isOpen && snapshotsStore.fetch());
</script>

<template>
  <Sheet v-model:open="open">
    <SheetTrigger as-child>
      <Button
        variant="ghost"
        size="icon"
        class="text-muted-foreground hover:text-primary"
        title="Version history">
        <History class="size-4" />
      </Button>
    </SheetTrigger>
    <SheetContent class="sm:max-w-md flex flex-col gap-0 p-0">
      <SheetHeader class="px-5 py-4 border-b">
        <SheetTitle class="flex items-center gap-2 text-base">
          <History class="size-4 text-muted-foreground" />
          Version history
          <span
            v-if="snapshots.length"
            class="ml-auto mr-6 text-xs font-normal text-muted-foreground">
            {{ snapshots.length }} {{ snapshots.length > 1 ? "versions" : "version" }}
          </span>
        </SheetTitle>
      </SheetHeader>

      <div
        v-if="loading && !snapshots.length"
        class="p-5 space-y-4">
        <Skeleton
          v-for="n in 4"
          :key="n"
          class="h-14 w-full" />
      </div>
      <EmptyState
        v-else-if="!snapshots.length"
        :icon="History"
        title="No version yet"
        description="Save the questionnaire to create its first version." />
      <div
        v-else
        class="flex-1 min-h-0 overflow-y-auto px-5 pb-6">
        <template
          v-for="(session, index) in sessions"
          :key="session.key">
          <div
            v-if="session.day !== sessions[index - 1]?.day"
            class="sticky top-0 z-10 -mx-5 px-5 pt-4 pb-2 bg-background/95 backdrop-blur text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
            {{ session.day }}
          </div>
          <HistorySession
            :versions="session.versions"
            :is-last="index === sessions.length - 1"
            :current-id="snapshots[0].id"
            :can-restore="canWriteSurvey"
            :restore-disabled-reason="restoreDisabledReason"
            :restoring-id="restoringId"
            @restore="pending = $event" />
        </template>
      </div>
    </SheetContent>

    <ConfirmDialog
      :open="pending !== null"
      title="Restore this version?"
      :description="`The questionnaire will be replaced by the version of ${pendingLabel}. The current state stays in the history.`"
      confirm-label="Restore"
      variant="default"
      :loading="restoringId !== null"
      :auto-close="false"
      @update:open="pending = $event ? pending : null"
      @confirm="confirmRestore" />
  </Sheet>
</template>
